"use client"

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Progress } from "@/components/ui/progress"
import { CheckCircle, Clock, Target, Zap, Trophy } from "lucide-react"
import {Link} from "@tanstack/react-router"
import { PersonalViewData } from "@/hooks/user-personal-view"
import { PipelineStats } from "@/types/job-overview"
import { useGetJobsJobIdPipeline } from "openapi/api/endpoints"

interface Job {
  id: number
  title: string
  company: string
  status: string
}


type PersonalViewProps=PersonalViewData& {
  gameModeEnabled: boolean
}

const JobPipelineCard = ({ job }: { job: Job }) => {
  const {data:pipelineState}=useGetJobsJobIdPipeline(job.id.toString())
  const pipelineStats:PipelineStats={
    hired:pipelineState?.['hired'].candidateIds?.length??0,
    interviewing:pipelineState?.['interviewing'].candidateIds?.length??0,
    longlist:pipelineState?.['longlist'].candidateIds?.length??0,
    outbound:pipelineState?.['outbound'].candidateIds?.length??0,
    rejected:pipelineState?.['rejected'].candidateIds?.length??0,
    screening:pipelineState?.['screening'].candidateIds?.length??0,
    shortlist:pipelineState?.['shortlist'].candidateIds?.length??0,
    submitted:pipelineState?.['submitted'].candidateIds?.length??0,
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Link to={`/jobs/${job.id}/overview`} className="font-medium hover:text-brand-ink hover:underline">
          {job.title}
        </Link>
        <Badge variant={job.status === "active" ? "default" : "secondary"}>{job.status}</Badge>
      </div>

      <div className="grid grid-cols-3 gap-2 text-xs">
        <div className="text-center">
          <div className="font-medium text-blue-600">{pipelineStats.longlist}</div>
          <div className="text-muted-foreground">Longlist</div>
        </div>
        <div className="text-center">
          <div className="font-medium text-yellow-600">{pipelineStats.interviewing}</div>
          <div className="text-muted-foreground">Interviewing</div>
        </div>
        <div className="text-center">
          <div className="font-medium text-green-600">{pipelineStats.hired}</div>
          <div className="text-muted-foreground">Hired</div>
        </div>
      </div>
    </div>
  )
}

const getPriorityColor = (priority: string) => {
  switch (priority) {
    case "high":
      return "bg-red-100 text-red-800 border-red-200"
    case "medium":
      return "bg-yellow-100 text-yellow-800 border-yellow-200"
    case "low":
      return "bg-green-100 text-green-800 border-green-200"
    default:
      return "bg-gray-100 text-gray-800 border-gray-200"
  }
}

export function PersonalView({
  todaysTasks,
  activeJobs,
  leaderboard,
  stats,
  xpProgress,
  gameModeEnabled,
}: PersonalViewProps) {
  return (
    <div className="space-y-4">
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Tasks Due Today</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.tasksDueToday}</div>
            <p className="text-xs text-muted-foreground">{stats?.highPriorityTasks} high priority</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Jobs</CardTitle>
            <Target className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.activeJobsCount}</div>
            <p className="text-xs text-muted-foreground">{stats?.totalCandidates} total candidates</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">This Week's XP</CardTitle>
            <Zap className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.weeklyXP}</div>
            <p className="text-xs text-muted-foreground">{stats?.xpGrowth} from last week</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Leaderboard Rank</CardTitle>
            <Trophy className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">#{stats?.leaderboardRank}</div>
            <p className="text-xs text-muted-foreground">of {stats?.totalRecruiters} recruiters</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-7">
        <Card className="col-span-4">
          <CardHeader>
            <CardTitle>Today's Tasks</CardTitle>
            <CardDescription>Your priority items for today</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {todaysTasks?.map((task) => (
                <div key={task.id} className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="flex-1">
                      <p className="font-medium">{task.title}</p>
                      <p className="text-sm text-muted-foreground">Due in {task.due}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Badge className={getPriorityColor(task?.priority??'')}>{task.priority}</Badge>
                    <Button size="sm" variant="outline">
                      <CheckCircle className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="col-span-3">
          <CardHeader>
            <CardTitle>Active Jobs</CardTitle>
            <CardDescription>Current job openings progress</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {activeJobs?.map((job) => (
                <JobPipelineCard key={job.id} job={job} />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {gameModeEnabled && xpProgress && (
          <Card>
            <CardHeader>
              <CardTitle>XP Progress</CardTitle>
              <CardDescription>
                Level {xpProgress.currentLevel} → Level {xpProgress.nextLevel}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>
                    {xpProgress.current.toLocaleString()} / {xpProgress.target.toLocaleString()} XP
                  </span>
                  <span>{Math.round((xpProgress.current / xpProgress.target) * 100)}%</span>
                </div>
                <Progress value={(xpProgress.current / xpProgress.target) * 100} className="h-3" />
                <p className="text-xs text-muted-foreground">
                  {(xpProgress.target - xpProgress.current).toLocaleString()} XP needed for next level
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {gameModeEnabled && (
          <Card>
            <CardHeader>
              <CardTitle>Team Leaderboard</CardTitle>
              <CardDescription>Top 5 this month</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {leaderboard?.slice(0, 5).map((member) => (
                  <div key={member.rank} className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <span className="text-lg">{member.badge}</span>
                      <span className="font-medium">{member.name}</span>
                    </div>
                    <div className="text-right">
                      <div className="font-medium">{member.xp} XP</div>
                      <div className="text-xs text-muted-foreground">{member.hires} hires</div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  )
}
